import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import classNames from 'classnames';
import useToast from './useToast';
import IconButton from '@widget/buttons/IconButton';
import Icon from '@icon/Icon';
import { MOVE_SLOP, SWIPE_DISTANCE } from "@hooks/touch/touchRules";
const Toast = React.memo((props) => {
    // props
    let { type, title, content, icon, showCloseBtn, autoClose, onClose, closeAfter, instance, createdAt, swipeable, peek, order, entering, paused, onSwipeUp } = props;
    // toast remove method
    const { remove } = useToast();
    // Default icon mapping for toast types
    const defaultIconMap = {
        success: ['fas', 'check-circle'],
        error: ['fas', 'times-circle'],
        warning: ['fas', 'exclamation-triangle'],
        info: ['fas', 'info-circle'],
        custom: ['fas', 'bell']
    };
    // ref 
    const ToastRef = React.useRef(null);
    // Auto-close: the time left, so hovering can pause it and leaving resumes it
    const timerRef = React.useRef(null);
    const remainingRef = React.useRef((closeAfter || 0) - (Date.now() - createdAt));
    const startedRef = React.useRef(0);
    const startTimer = () => {
        if (!autoClose)
            return;
        clearTimeout(timerRef.current);
        if (remainingRef.current <= 0) {
            handleRemove();
            return;
        }
        startedRef.current = Date.now();
        timerRef.current = setTimeout(() => handleRemove(), remainingRef.current);
    };
    const pauseTimer = () => {
        if (!autoClose || !timerRef.current)
            return;
        clearTimeout(timerRef.current);
        timerRef.current = null;
        remainingRef.current -= Date.now() - startedRef.current;
    };
    // Runs the auto-close countdown, paused while the stack is open
    React.useEffect(() => {
        if (paused)
            pauseTimer();
        else
            startTimer();
    }, [instance, paused]);
    React.useEffect(() => () => clearTimeout(timerRef.current), []);
    // handle remove (direction: where a swipe sends it)
    const handleRemove = (direction) => {
        clearTimeout(timerRef.current);
        const el = ToastRef.current;
        if (!el)
            return;
        if (direction) {
            el.style.transition = 'transform 200ms ease-out, opacity 200ms ease-out';
            el.style.transform = `translateX(${direction === 'left' ? '-120%' : '120%'})`;
            el.style.opacity = '0';
        }
        else {
            el.classList.remove("uxpcore_toast--show");
            el.classList.add("uxpcore_toast--hide");
        }
        setTimeout(() => {
            remove(instance);
            if (onClose)
                onClose();
        }, direction ? 200 : 300);
    };
    // Phones: the toast follows the finger sideways; far enough and it goes, else it snaps back.
    // Upwards on the front toast of the collapsed stack dismisses them all.
    const drag = React.useRef(null);
    const swipedRef = React.useRef(false);
    const onPointerDown = (e) => {
        if (!swipeable || e.pointerType === 'mouse')
            return;
        drag.current = { x: e.clientX, y: e.clientY, moved: false };
        swipedRef.current = false;
    };
    const onPointerMove = (e) => {
        const d = drag.current;
        const el = ToastRef.current;
        if (!d || !el)
            return;
        const dx = e.clientX - d.x, dy = e.clientY - d.y;
        if (!d.axis && Math.max(Math.abs(dx), Math.abs(dy)) > MOVE_SLOP)
            d.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (d.axis === 'x') {
            d.moved = true;
            el.style.transition = 'none';
            el.style.transform = `translateX(${dx}px)`;
            el.style.opacity = String(Math.max(0.3, 1 - Math.abs(dx) / 300));
        }
        else if (d.axis === 'y' && onSwipeUp && dy < 0) {
            d.moved = true;
            el.style.transition = 'none';
            el.style.transform = `translateY(${dy}px)`;
        }
    };
    const onPointerUp = (e) => {
        const d = drag.current;
        const el = ToastRef.current;
        drag.current = null;
        if (!d || !el || !d.moved)
            return;
        swipedRef.current = true;
        const dx = e.clientX - d.x, dy = e.clientY - d.y;
        if (d.axis === 'x' && Math.abs(dx) > SWIPE_DISTANCE)
            return handleRemove(dx < 0 ? 'left' : 'right');
        if (d.axis === 'y' && onSwipeUp && dy < -SWIPE_DISTANCE)
            return onSwipeUp();
        el.style.transition = 'transform 200ms ease-out, opacity 200ms ease-out';
        el.style.transform = '';
        el.style.opacity = '';
    };
    // return
    return (_jsxs("div", { className: classNames("uxpcore_toast", `uxpcore_toast--${type}`, "uxpcore_toast--show", peek !== undefined && `uxpcore_toast--peek-${peek}`, entering && "uxpcore_toast--entering"), ref: ToastRef, style: order !== undefined ? { order } : undefined, onPointerDown: onPointerDown, onPointerMove: onPointerMove, onPointerUp: onPointerUp, onPointerCancel: () => { drag.current = null; }, 
        // A swipe isn't also a tap (which opens the stack)
        onClickCapture: (e) => { if (swipedRef.current) {
            swipedRef.current = false;
            e.stopPropagation();
        } }, children: [type == "custom" ?
                _jsx("div", { className: "uxpcore_toast__content", children: typeof content == "string" ? content : content() })
                :
                    _jsxs(_Fragment, { children: [_jsx("div", { className: "uxpcore_toast__icon", children: (() => {
                                    if (icon && typeof icon === 'function') {
                                        return icon();
                                    }
                                    const iconToRender = icon || defaultIconMap[type];
                                    return _jsx(Icon, { icon: iconToRender });
                                })() }), _jsxs("div", { className: "uxpcore_toast__content", children: [title &&
                                        _jsx("div", { className: "uxpcore_toast__title", children: typeof title == "string" ? title : title() }), _jsx("div", { className: "uxpcore_toast__body", children: typeof content == "string" ? content : content() })] })] }), showCloseBtn && !swipeable && (_jsx(IconButton, { type: "close", onClick: () => handleRemove(), variant: "secondary", size: "small", className: "uxpcore_toast__close" }))] }));
});
export default Toast;
